import { supabase } from '@/integrations/supabase/client';

export const FUNCTIONS_BASE = 'https://yhcymqfdigeufzooutxi.functions.supabase.co';

async function getAccessToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

/**
 * callEdge - call a Supabase Edge Function by name using direct fetch.
 * Automatically attaches Authorization when authRequired = true.
 * S-2239: Throws on non-2xx status codes instead of treating as success.
 * S-2241: Injects run_id into JSON body if missing and adds payload logging.
 */
export async function callEdge(
  name: string,
  options?: {
    method?: 'GET' | 'POST';
    body?: any;
    authRequired?: boolean;
    headers?: Record<string,string>;
  }
) {
  const method = options?.method ?? 'POST';
  const authRequired = options?.authRequired ?? false;
  
  // S-2241: Generate run_id once per call
  const runId = options?.headers?.['x-run-id'] ?? crypto.randomUUID?.() ?? String(Date.now());
  
  const headers: Record<string,string> = {
    'content-type': 'application/json',
    'x-run-id': runId, // S-2241: Always set header
    ...(options?.headers || {})
  };

  if (authRequired) {
    const token = await getAccessToken();
    if (!token) {
      throw new Error('Authentication required - no session');
    }
    headers['authorization'] = `Bearer ${token}`;
  }

  // ARCHITECT: Ensure body is an object and inject run_id
  let body = options?.body ?? {};
  if (method === 'POST') {
    // Handle string bodies
    if (typeof body === 'string') {
      try { 
        body = JSON.parse(body);
        // Check for double-stringified JSON
        if (typeof body === 'string') {
          try {
            body = JSON.parse(body);
          } catch { /* ignore double-stringified JSON */ }
        }
      } catch { 
        body = {}; 
      }
    }
    
    if (body && typeof body === 'object' && !body.run_id) {
      body = { ...body, run_id: runId };
    }

    // ARCHITECT: Enhanced payload logging
    console.log(JSON.stringify({
      at: "PoseClient:payload",
      run_id: runId,
      ct: "application/json",
      keys: body && typeof body === 'object' ? Object.keys(body).sort() : [],
      frames_length: Array.isArray(body?.frames) ? body.frames.length : 0
    }));
  }

  const url = `${FUNCTIONS_BASE}/${name}`;
  const res = await fetch(url, {
    method,
    headers,
    body: method === 'POST' ? JSON.stringify(body) : undefined,
    // avoid caches on test flows
    cache: 'no-store',
  });

  const text = await res.text();
  let json: any = null;
  try { json = JSON.parse(text); } catch { /* non-JSON response */ }

  // S-2239: Throw on non-2xx status codes
  if (!res.ok) {
    const errorMessage = json?.message || `Edge function error ${res.status}`;
    throw new Error(errorMessage);
  }

  return json ?? text;
}

/**
 * Enhanced edgeInvoke with structured logging and proper error handling.
 * S-2239: Ensures non-2xx responses throw errors, not false success.
 * S-2241: Injects run_id into JSON body if missing and adds payload logging.
 */
export async function edgeInvoke(url: string, init: RequestInit & { runId?: string } = {}) {
  const runId = init.runId ?? crypto.randomUUID?.() ?? String(Date.now());
  const headers = {
    "content-type": "application/json",
    "x-run-id": runId,
    ...(init.headers || {}),
  } as Record<string,string>;

  // ARCHITECT: Ensure body is an object and inject run_id
  let body = undefined;
  if (typeof init.body === "string") {
    // Parse string body to object
    try { 
      body = JSON.parse(init.body);
      // Check for double-stringified JSON
      if (typeof body === "string") {
        try {
          body = JSON.parse(body);
        } catch { /* ignore double-stringified JSON */ }
      }
    } catch { 
      body = null; 
    }
  } else if (init.body && typeof init.body === "object") {
    body = init.body;
  }

  if (body && typeof body === "object" && !body.run_id) {
    body.run_id = runId;
  }
  
  // ARCHITECT: Single stringify, guaranteed
  const sendBody = body && typeof body === "object" ? JSON.stringify(body) : init.body;

  // ARCHITECT: Enhanced payload logging
  console.log(JSON.stringify({
    at: "PoseClient:payload",
    run_id: runId,
    ct: "application/json",
    keys: body && typeof body === "object" ? Object.keys(body).sort() : [],
    frames_length: Array.isArray(body?.frames) ? body.frames.length : 0
  }));

  const t0 = performance.now();
  try {
    const res = await fetch(url, { ...init, headers, body: sendBody });
    const duration_ms = performance.now() - t0;
    let json: any = null;
    try { json = await res.json(); } catch { /* non-JSON response */ }

    // S-2239: Check both HTTP status and response.ok field
    if (!res.ok || (json && json.ok === false)) {
      console.error(JSON.stringify({ 
        at: "withLog:error", 
        label: new URL(url).pathname.slice(1), 
        run_id: runId, 
        duration_ms, 
        status: res.status, 
        body: json 
      }));
      const errorMessage = json?.message || `Edge error ${res.status}`;
      throw new Error(errorMessage);
    }

    console.log(JSON.stringify({ 
      at: "withLog:success", 
      label: new URL(url).pathname.slice(1), 
      run_id: runId, 
      duration_ms, 
      payload_summary: Object.keys(json || {}) 
    }));
    return json;

  } catch (err) {
    // Normalize to TypeError to integrate with S-2237 watchdog when used for analysis calls
    throw new TypeError(String(err?.message || err));
  }
}

/**
 * Wrap any async network call to emit structured logs correlated by run_id.
 * Usage:
 *   await withLog("pose-save", () => fetch(...), { runId, payload });
 */
export async function withLog<T>(
  label: string,
  fn: () => Promise<T>,
  opts?: { runId?: string; payload?: any }
): Promise<T> {
  const { runId = "no-run-id", payload } = opts ?? {};
  const t0 = (typeof performance !== "undefined" ? performance.now() : Date.now());

  try {
    const out = await fn();
    const dur = ((typeof performance !== "undefined" ? performance.now() : Date.now()) - t0).toFixed(1);
    console.info(JSON.stringify({
      at: "withLog:success",
      label,
      run_id: runId,
      duration_ms: Number(dur),
      payload_summary: payload
        ? (Array.isArray(payload) ? `len:${payload.length}` : Object.keys(payload))
        : undefined,
    }));
    return out;
  } catch (err: any) {
    const dur = ((typeof performance !== "undefined" ? performance.now() : Date.now()) - t0).toFixed(1);
    console.error(JSON.stringify({
      at: "withLog:error",
      label,
      run_id: runId,
      duration_ms: Number(dur),
      error: err?.message || String(err),
    }));
    throw err;
  }
}